import { useEffect, useState } from "react";
import { CheckCircle2, Flag, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  useReportPost,
  type ReportReason,
} from "@/hooks/useReports";

const REASONS: { value: ReportReason; label: string; hint: string }[] = [
  { value: "spam",           label: "Spam or repetitive content",   hint: "Repeated, promotional, or off-topic posting." },
  { value: "harassment",     label: "Harassment or personal attack", hint: "Targeted abuse, insults, or threats." },
  { value: "offtopic",       label: "Off-topic for this thread",    hint: "Doesn't belong in this discussion." },
  { value: "misinformation", label: "Misinformation",               hint: "False or misleading claims that need correction." },
  { value: "other",          label: "Other",                        hint: "Use the note field to explain." },
];

interface ReportPostDialogProps {
  postId: string | null;
  onOpenChange: (open: boolean) => void;
}

/** Small modal triggered from a post's kebab menu. Idempotent on the
 *  backend — re-reporting the same post is a no-op while a prior
 *  report is pending. */
export function ReportPostDialog({ postId, onOpenChange }: ReportPostDialogProps) {
  const report = useReportPost();
  const [reason, setReason] = useState<ReportReason>("spam");
  const [note,   setNote]   = useState("");

  useEffect(() => {
    if (postId == null) return;
    setReason("spam");
    setNote("");
    report.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId]);

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (postId == null) return;
    report.mutate({ postId, reason, note: note.trim() || undefined });
  }

  const err = report.error as
    | { response?: { data?: { detail?: string } } } | undefined;
  const errorMsg = err?.response?.data?.detail;

  return (
    <Dialog open={postId !== null} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-rose-600" />
            Report this post
          </DialogTitle>
          <DialogDescription>
            A moderator will review it. You can re-flag a post if circumstances change.
          </DialogDescription>
        </DialogHeader>

        {report.isSuccess ? (
          <div className="space-y-4 py-2 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-600" />
            <p className="text-sm">
              Thanks — the moderators have been notified.
            </p>
            <Button onClick={() => onOpenChange(false)} className="w-full">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Why are you reporting it?</Label>
              <div className="space-y-1">
                {REASONS.map((r) => (
                  <label
                    key={r.value}
                    className="flex cursor-pointer items-start gap-3 rounded-md border p-2.5 hover:bg-accent/30 has-[input:checked]:border-primary/40 has-[input:checked]:bg-primary/5"
                  >
                    <input
                      type="radio" name="report-reason" value={r.value}
                      checked={reason === r.value}
                      onChange={() => setReason(r.value)}
                      className="mt-0.5 h-4 w-4 accent-primary"
                    />
                    <div className="text-sm">
                      <div className="font-medium">{r.label}</div>
                      <div className="text-xs text-muted-foreground">{r.hint}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="rp-note">
                Note <span className="text-xs text-muted-foreground">— optional</span>
              </Label>
              <Textarea
                id="rp-note" rows={3} maxLength={1000}
                value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Anything that helps the moderator understand the context."
              />
            </div>

            {errorMsg && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {errorMsg}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={report.isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={report.isPending}>
                {report.isPending
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</>
                  : <><Flag className="mr-2 h-4 w-4" />Submit report</>}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
